import React, { PureComponent } from 'react';
import classnames from 'classnames';
import { withSize } from 'react-sizeme'
import * as d3Scale from 'd3-scale';

import { sortByKey, isNumeric, numberFormat } from '~/core/scripts/utils';

const margin = {
  top: 20,
  left: 0,
  right: 0,
  bottom: 8
};

const textAnchors = ['start', 'middle', 'end'];
const translate = (x, y) => `translate(${x},${y})`;

function findClosestBar(data, valueKey, value) {
  return data.reduce((res, item, index) => {
    if (Math.abs(item[valueKey] - value) < res.diff) {
      res.diff = Math.abs(item[valueKey] - value);
      res.item = item;
      res.index = index;
    }

    return res;
  }, { diff: Number.MAX_VALUE, item: null, index: null });
}

class Chart extends PureComponent {

  constructor(props) {
    super();

    const { valueKey, data, avg, avgGreen, inverted = false, colors, size } = props;
    const { width } = size;

    this.height = width * 0.15;
    this.yScale = d3Scale.scaleLinear().domain([0, props.max]).range([0, this.height]);
    this.xScale = d3Scale.scaleLinear().domain([0, 2]).range([0, width]);

    this.data = data
      .filter(d => isNumeric(d[valueKey]) && !['888', '999'].includes(d.AGS))
      .sort(sortByKey(valueKey, inverted));
    this.barWidth = width / this.data.length;

    const closestToAvg = findClosestBar(this.data, valueKey, avg);
    const closestToAvgGreen = findClosestBar(this.data, valueKey, avgGreen);

    this.colors = inverted ? colors.slice(0) : colors.slice(0).reverse();

    this.avgIndex = closestToAvg.index;
    this.avgGreenIndex = closestToAvgGreen.index;
  }

  renderBar(d, index) {
    const value = d[this.props.valueKey];
    const barHeight = this.yScale(value);
    const fillColor = this.props.filterFunc(d) ? this.props.colorizer(d) : '#f2f2f2';

    return (
      <rect
        key={`bar_${this.props.valueKey}_${index}`}
        height={barHeight}
        width={this.barWidth}
        x={this.barWidth * index}
        y={this.height - barHeight}
        fill={fillColor}
      />
    );
  }

  renderBars() {
    if (!this.props.isVisible) {
      return null;
    }

    return <g>{this.data .map((d, i) => this.renderBar(d, i))}</g>;
  }

  renderLabels() {
    const { min, avg, max, inverted, annotateChart, decimals, unit } = this.props;
    const values = inverted ? [max, avg, min] : [min, avg, max];
    const dys = inverted ? [-8, -14, -16] : [-16, -14, -8];


    return (
      <g className="chart__labels">
        {values.map((d, i) => {
          const isAvg = i === 1;
          const isMax = i === 2;
          const x = isAvg ? this.barWidth * this.avgIndex : this.xScale(i);
          const colorIndex = isMax ? 0 : this.colors.length - 1;
          const valueClasses = classnames('chart__labels-value', { avg: isAvg });

          return (
            <g
              key={`value_${this.props.valueKey}_${d}`}
              transform={translate(x, this.height - this.yScale(d))}
              textAnchor={textAnchors[i]}
              fill={isAvg ? '#111' : this.colors[colorIndex]}
            >
              <text dy={dys[i]} className={valueClasses}>
                {isAvg ? 'ø ' : null}{numberFormat(d, decimals)} {unit}
              </text>
              {(isAvg && annotateChart) && <text dy={-28} className="chart__labels-avg">Deutschlandwert</text>}
            </g>
          );
        })}
      </g>
    );
  }

  renderAvgGreenLabel() {
    if (!this.props.isFiltered) {
      return null;
    }

    const { avgGreen, decimals, unit } = this.props;
    const x = this.barWidth * this.avgGreenIndex;
    const y = this.height - this.yScale(avgGreen);

    return (
      <g
        transform={translate(x, y)}
        textAnchor="middle"
        fill="#222"
      >
        <text dy={-14} className="chart__labels-value green">
          ø {numberFormat(avgGreen, decimals)} {unit}
        </text>
      </g>
    );
  }

  renderAvgBar(value, index, className = null) {
    const barHeight = this.yScale(value) + margin.bottom;
    const barClasses = classnames('chart__avgbar', className);

    return (
      <g
        className={barClasses}
        transform={translate(index * this.barWidth, this.height - barHeight)}
      >
        <line
          x1={0}
          y1={0}
          y2={barHeight}
        />
      </g>
    );
  }

  render() {
    const { size, inverted, isFiltered } = this.props;
    const svgHeight = this.height + margin.top + margin.bottom;
    const yLabels = inverted ? ['überdurchschnittlich', 'unterdurchschnittlich'] : ['unterdurchschnittlich', 'überdurchschnittlich'];
    return (
      <div className="chart">
        <svg
          className="chart__svg"
          width={size.width}
          height={svgHeight}
          viewBox={`0 0 ${size.width} ${svgHeight}`}
        >
          <g transform={translate(0, margin.top)}>
            {this.renderLabels()}
            {this.renderAvgGreenLabel()}
            {this.renderBars()}
            {this.renderAvgBar(this.props.avg, this.avgIndex)}
            {isFiltered && this.renderAvgBar(this.props.avgGreen, this.avgGreenIndex, 'green')}
          </g>
        </svg>
        <div className="chart__yaxis">
          {/* <div className="chart__yaxis-tick min below" style={{ color: this.colors[this.colors.length - 1] }}>{yLabels[0]}</div> */}
          <div className="chart__yaxis-tick min below">{yLabels[0]}</div>
          {/* <div className="chart__yaxis-tick max above" style={{ color: this.colors[0] }}>{yLabels[1]}</div> */}
          <div className="chart__yaxis-tick max above">{yLabels[1]}</div>
        </div>
      </div>
    );
  }
}

export default withSize()(Chart);
